import { getStore, updateStore } from 'core_main/store';
import { KustomerErrorResponse, PublicCallback } from 'globals/types';
import { ErrorMessage, handleError, debug, LogLabel } from 'globals/errors';
import Pubnub from 'pubnub';
import { backoffAuthCall } from 'core_main/api/pubnub';
import { handleCustomerDeletedEvent } from 'core_main/pubnub/events/customerDeleted';
import { handleChatSessionDeletedEvent } from 'core_main/pubnub/events/chatSessionDelete';
import {
  addConversationPubNubListener,
  addMetaPubNubListener,
  clearPubNub,
  getConversationPubNub,
  getMetaPubNub,
  getPubNubClientGeneration,
} from './helpers/instance';
import {
  KUSTOMER_APP_CHAT_CSAT_SEND_EVENT,
  KUSTOMER_APP_CHAT_MESSAGE_RECEIVE_EVENT,
  KUSTOMER_APP_CHAT_MESSAGE_SEND_EVENT,
  KUSTOMER_APP_CHAT_SESSION_DELETE_EVENT,
  KUSTOMER_APP_CHAT_SESSION_END_EVENT,
  KUSTOMER_APP_CHAT_SESSION_MERGED_EVENT,
  KUSTOMER_APP_CHAT_SESSION_UPDATE_EVENT,
  KUSTOMER_APP_CHAT_URL_UNFURLED_SEND_EVENT,
  KUSTOMER_APP_CHAT_URL_UNFURLED_RECEIVE_EVENT,
  KUSTOMER_APP_CUSTOMER_DELETED,
  KUSTOMER_ASSISTANT_CHAT_SESSION_END_EVENT,
  PUBNUB_CONNECTED_CATEGORY,
  PUBNUB_ACCESS_DENIED_CATEGORY,
  PUBNUB_BAD_REQUEST_CATEGORY,
  PUBNUB_SUBSCRIBE_OPERATION,
  USER_TYPING,
  USER_TYPING_ENDED,
  AI_ASSISTANT_USER_TYPING,
} from './constants';
import { handlePubNubConversationSubscribeOperation } from './events/conversationSubscribeOperation';
import {
  createConversationChannelGroupString,
  createMetaCustomerChannelString,
} from './helpers/channelName';

import { InitializePubNubParameters } from './types';
import { handleChatMessageReceivedEvent } from './events/chatMessageReceived';
// eslint-disable-next-line import/no-cycle
import { handleChatSessionUpdateEvent } from './events/chatSessionUpdate';
import { handleChatSessionMergedEvent } from './events/chatSessionMerged';
import { handleChatSessionEndedEvent } from './events/chatSessionEnded';
import { handleAssistantSessionEndedEvent } from './events/assistantSessionEnded';
import { handleChatCsatSendEvent } from './events/chatCsatSend';
import { handleUserTypingEvent } from './events/userTyping';
import {
  createPubnubNetworkStatusEventHandler,
  setHealthCheckFunction as setNetworkStatusHealthCheck,
} from './events/networkStatus';
import { handleUrlUnfurled } from './events/handleUrlUnfurled';
import {
  setupGrantTokenRefresh,
  teardownGrantTokenRefresh,
  setHealthCheckFunction as setGrantTokenHealthCheck,
} from './helpers/grantToken';
import {
  markConversationChannelGroupConnected,
  resetEmptyChannelGroupRecovery,
  scheduleEmptyChannelGroupResubscribe,
} from './helpers/emptyChannelGroupRecovery';
import { resubscribeConversationChannelGroup } from './helpers/resubscribeConversationChannelGroup';
// Health check is lazy-loaded to reduce initial bundle size
// It's only needed when PubNub connection issues occur

/** Max wait for PNConnectedCategory on force re-init before proceeding without the ack. */
const FORCE_REINITIALIZE_SUBSCRIBE_ACK_TIMEOUT_MS = 5000;
export {
  getConversationPubNub,
  getMetaPubNub,
  clearPubNub,
  stopPubNub,
} from './helpers/instance';
export { resubscribeConversationChannelGroup } from './helpers/resubscribeConversationChannelGroup';

/**
 * Lazy-loads and calls the health check wrapper.
 * This reduces initial bundle size by only loading health check code when needed.
 */
export async function checkAndReconnectIfNeededWrapper(): Promise<void> {
  try {
    const { checkAndReconnectIfNeededWrapper: healthCheck } = await import(
      /* webpackChunkName: "pubnub-health-check" */ './helpers/checkAndReconnect'
    );
    await healthCheck();
  } catch (error) {
    handleError(error, {
      message: 'Failed to lazy-load PubNub health check',
      labels: [LogLabel.PUBNUB_RECOVERY],
    });
  }
}

export interface CreatePubNubMetaListenerOptions {
  /** No-op handlers when this listener's client generation is obsolete. */
  isListenerCurrent?: () => boolean;
}

export const createPubNubMetaListeners = (
  outboundChatEnabled?: boolean,
  options?: CreatePubNubMetaListenerOptions,
) => {
  const networkStatusHandler = createPubnubNetworkStatusEventHandler(
    !!outboundChatEnabled,
  );

  return {
    message: (messageEvent: Pubnub.MessageEvent) => {
      if (options?.isListenerCurrent && !options.isListenerCurrent()) {
        return;
      }

      const { message } = messageEvent;

      const { name } = message;

      try {
        switch (name) {
          case KUSTOMER_APP_CHAT_SESSION_END_EVENT:
            handleChatSessionEndedEvent(message);
            break;
          case KUSTOMER_ASSISTANT_CHAT_SESSION_END_EVENT:
            handleAssistantSessionEndedEvent(message);
            break;
          case KUSTOMER_APP_CHAT_SESSION_UPDATE_EVENT:
            handleChatSessionUpdateEvent(message);
            break;
          case KUSTOMER_APP_CHAT_SESSION_MERGED_EVENT:
            handleChatSessionMergedEvent(message);
            break;
          case KUSTOMER_APP_CUSTOMER_DELETED:
            handleCustomerDeletedEvent();
            break;
          case KUSTOMER_APP_CHAT_SESSION_DELETE_EVENT:
            handleChatSessionDeletedEvent(message);
            break;
          default:
            break;
        }
      } catch (e) {
        handleError(e);
      }
    },
    signal: (signalEvent) => {
      if (options?.isListenerCurrent && !options.isListenerCurrent()) {
        return;
      }

      const { message } = signalEvent;

      switch (message) {
        case USER_TYPING:
        case AI_ASSISTANT_USER_TYPING:
        case USER_TYPING_ENDED: {
          handleUserTypingEvent(signalEvent, {
            isAIGeneratingAnswer: message === AI_ASSISTANT_USER_TYPING,
          });
          break;
        }
        default:
          break;
      }
    },
    status: (statusEvent: Pubnub.StatusEvent) => {
      if (options?.isListenerCurrent && !options.isListenerCurrent()) {
        return;
      }
      networkStatusHandler(statusEvent);
    },
  };
};

export interface CreatePubNubConversationListenerOptions {
  /**
   * Force re-key: skip subscribe-time delivery marking so ordered catch-up
   * can replay missed messages before they gain delivery actions.
   */
  skipDeliveryMarking?: boolean;
  /** No-op message/status handling when this listener's client generation is obsolete. */
  isListenerCurrent?: () => boolean;
}

export const createPubNubConversationListeners = (
  customerId: string,
  orgId: string,
  cb?: PublicCallback,
  onConnectedAck?: (statusEvent: Pubnub.StatusEvent) => void,
  options?: CreatePubNubConversationListenerOptions,
) => ({
  message: (messageEvent: Pubnub.MessageEvent) => {
    if (options?.isListenerCurrent && !options.isListenerCurrent()) {
      return;
    }

    const { message, timetoken } = messageEvent;

    const { name } = message;

    try {
      switch (name) {
        case KUSTOMER_APP_CHAT_MESSAGE_RECEIVE_EVENT:
          handleChatMessageReceivedEvent(messageEvent);
          break;
        case KUSTOMER_APP_CHAT_MESSAGE_SEND_EVENT:
          handleChatMessageReceivedEvent(messageEvent);
          break;
        case KUSTOMER_APP_CHAT_CSAT_SEND_EVENT:
          handleChatCsatSendEvent(message, timetoken);
          break;
        case KUSTOMER_APP_CHAT_URL_UNFURLED_SEND_EVENT:
        case KUSTOMER_APP_CHAT_URL_UNFURLED_RECEIVE_EVENT:
          handleUrlUnfurled(messageEvent);
          break;
        default:
          break;
      }
    } catch (e) {
      handleError(e);
    }
  },
  status: (statusEvent: Pubnub.StatusEvent) => {
    if (options?.isListenerCurrent && !options.isListenerCurrent()) {
      return;
    }

    const { operation } = statusEvent;

    if (operation === PUBNUB_SUBSCRIBE_OPERATION) {
      if (onConnectedAck) {
        onConnectedAck(statusEvent);
      }
      handlePubNubConversationSubscribeOperation(
        statusEvent,
        orgId,
        customerId,
        cb,
        {
          skipDeliveryMarking: options?.skipDeliveryMarking,
        },
      );
    }
  },
});

/** True when the failed request is Kustomer PubNub auth HTTP 400 (EUX-1702 / EUX-1698). */
function isPubNubAuthHttp400(error: unknown): boolean {
  const e = error as KustomerErrorResponse;
  return (
    e?.status === '400' &&
    typeof e?.url === 'string' &&
    e.url.includes('/pubnub/auth')
  );
}

export const initializePubNub = ({
  customerId,
  orgId,
  cb,
  forceReinitialize,
}: InitializePubNubParameters) => {
  const store = getStore();

  const { settings } = store;

  const { outboundChatEnabled } = settings;

  debug({
    message: `initializePubNub called, customerId: ${customerId}, orgId: ${orgId}, isPubNubConnected: ${store.isPubNubConnected}`,
    labels: [LogLabel.PUBNUB_RECOVERY],
  });

  if (
    !customerId ||
    !orgId ||
    (store.isPubNubConnected && !forceReinitialize)
  ) {
    debug({
      message: `Early return from initializePubNub, hasCustomerId: ${!!customerId}, hasOrgId: ${!!orgId}, isPubNubConnected: ${
        store.isPubNubConnected
      }`,
      labels: [LogLabel.PUBNUB_RECOVERY],
    });
    if (typeof cb === 'function') cb();
    return Promise.resolve();
  }

  teardownGrantTokenRefresh();
  // Force re-key of the same outage must not clear the exhaustion latch or we
  // re-page on every rebuild. Fresh (non-force) init clears it for a new session.
  resetEmptyChannelGroupRecovery({
    preserveExhaustionLatch: !!forceReinitialize,
  });
  clearPubNub();

  // During force re-key, the prior session's injected health check can fire
  // inside refreshGrantToken while store.customerId is still pre-merge.
  // Suspend it until this attempt finishes; restore on success or failure.
  const installRealtimeHealthChecks = () => {
    setGrantTokenHealthCheck(async () => {
      await checkAndReconnectIfNeededWrapper();
    });
    setNetworkStatusHealthCheck(async () => {
      await checkAndReconnectIfNeededWrapper();
    });
  };

  if (forceReinitialize) {
    setGrantTokenHealthCheck(async () => {});
    setNetworkStatusHealthCheck(async () => {});
  }

  return setupGrantTokenRefresh(outboundChatEnabled)
    .catch(() => {
      // if an unrecoverable error occurs, try v1 auth
      return backoffAuthCall({ version: 1, outboundChatEnabled: false });
    })
    .catch((error: unknown) => {
      // HTTP 400 on `/pubnub/auth` is often expected (org without realtime / misconfig). Degrade instead of
      // rejecting so the widget does not surface an unhandled error; Sentry `http.client` for this case is
      // dropped in `shouldDropPubNubHttpClientNoise` (EUX-1702, EUX-1698).
      if (isPubNubAuthHttp400(error)) {
        console.warn(ErrorMessage.PUBNUB_AUTH_NOT_CONFIGURED, error);
        return undefined;
      }
      return Promise.reject(error);
    })
    .then(async (authResponse) => {
      debug({
        message: `Auth response received: ${
          authResponse ? 'success' : 'failed'
        }`,
        labels: [LogLabel.PUBNUB_RECOVERY],
      });
      // `undefined` auth: skip PubNub listeners/subscribe (graceful degradation after 400 path above).
      if (!authResponse) {
        debug({
          message: 'No auth response, returning early',
          labels: [LogLabel.PUBNUB_RECOVERY],
        });
        if (forceReinitialize) {
          installRealtimeHealthChecks();
        }
        if (typeof cb === 'function') cb();
        return;
      }

      const channelGroupString = createConversationChannelGroupString(
        customerId,
        orgId,
      );
      const customerMetaChannel = createMetaCustomerChannelString(
        customerId,
        orgId,
      );

      // Grant refresh may have constructed instances from store.customerId.
      // On force re-init the store still holds the pre-merge id — tear down and
      // rebuild with the explicit target, then re-apply the grant token.
      if (forceReinitialize) {
        clearPubNub();
        const meta = getMetaPubNub(customerId);
        const conversation = getConversationPubNub(customerId);
        // No tracking token (or failed construction) → subscribe would no-op and
        // the ack waiter would always time out. Fail fast instead.
        if (!meta?.instance || !conversation?.instance) {
          updateStore('isPubNubConnected', false);
          throw new Error(
            'Cannot re-key realtime without PubNub clients (missing tracking token)',
          );
        }
        if (authResponse.token) {
          meta.instance.setToken(authResponse.token);
          conversation.instance.setToken(authResponse.token);
        }
      }

      // Force re-init (merge re-key): wait briefly for an attempt-scoped connected
      // ack for the target conversation group. Resolver lives in this listener
      // closure so a stale prior attempt cannot satisfy a later waiter. A timeout
      // must not tear down the new clients — the subscribe may already be healthy
      // and a late ack (or health check) can still mark connected.
      // Capture generation after the last clear above so a replaced client's late
      // ack cannot mark the newer identity connected.
      const listenerGeneration = getPubNubClientGeneration();
      const isListenerCurrent = () =>
        getPubNubClientGeneration() === listenerGeneration;

      let resolveForceAck: ((acked: boolean) => void) | undefined;
      let forceAckTimeoutId: ReturnType<typeof setTimeout> | undefined;
      let forceAckTimedOut = false;
      let forceAccessDeniedRecoveryStarted = false;

      const resubscribeForceChannels = () => {
        getConversationPubNub(customerId)?.instance?.subscribe({
          channelGroups: [channelGroupString],
        });
        getMetaPubNub(customerId)?.instance?.subscribe({
          channels: [customerMetaChannel],
        });
      };

      /**
       * Post-merge PAM race: auth can succeed while subscribe still 403s until
       * grants settle. Refresh the grant once and resubscribe without ending the
       * force-ack wait so a subsequent PNConnectedCategory can still win.
       */
      const recoverForceAccessDenied = async () => {
        if (forceAccessDeniedRecoveryStarted || !isListenerCurrent()) {
          return;
        }
        forceAccessDeniedRecoveryStarted = true;
        debug({
          message:
            'Force re-init AccessDenied: refreshing grant and resubscribing',
          labels: [LogLabel.PUBNUB_RECOVERY],
        });
        try {
          const refreshed = await setupGrantTokenRefresh(!!outboundChatEnabled);
          if (!isListenerCurrent()) {
            return;
          }
          if (!refreshed?.token) {
            debug({
              message:
                'Force re-init AccessDenied recovery skipped resubscribe: no refreshed token',
              labels: [LogLabel.PUBNUB_RECOVERY],
            });
            return;
          }
          getMetaPubNub(customerId)?.instance?.setToken(refreshed.token);
          getConversationPubNub(customerId)?.instance?.setToken(
            refreshed.token,
          );
          resubscribeForceChannels();
        } catch (recoveryError) {
          debug({
            message: `Force re-init AccessDenied recovery failed: ${
              recoveryError instanceof Error
                ? recoveryError.message
                : String(recoveryError)
            }`,
            labels: [LogLabel.PUBNUB_RECOVERY],
          });
        }
      };

      const forceAckPromise = forceReinitialize
        ? new Promise<boolean>((resolve) => {
            resolveForceAck = resolve;
          })
        : null;

      // Both force and normal init wait for a conversation-group Connected ack
      // before claiming isPubNubConnected. Empty-group BadRequest schedules a
      // bounded resubscribe instead of leaving a false healthy state.
      const onConnectedAck = (statusEvent: Pubnub.StatusEvent) => {
        if (!isListenerCurrent()) {
          debug({
            message: `Subscribe ack ignored: stale client generation (listener=${listenerGeneration}, current=${getPubNubClientGeneration()})`,
            labels: [LogLabel.PUBNUB_RECOVERY],
          });
          return;
        }

        if (statusEvent.category === PUBNUB_BAD_REQUEST_CATEGORY) {
          debug({
            message: `Subscribe ack: category=${statusEvent.category}, operation=${statusEvent.operation}, scheduling empty channel-group recovery`,
            labels: [LogLabel.PUBNUB_RECOVERY],
          });
          scheduleEmptyChannelGroupResubscribe({
            isListenerCurrent,
            customerId,
            orgId,
          });
          return;
        }

        if (
          forceReinitialize &&
          statusEvent.category === PUBNUB_ACCESS_DENIED_CATEGORY
        ) {
          debug({
            message: `Force re-init ack: category=${statusEvent.category}, operation=${statusEvent.operation}, starting grant recovery`,
            labels: [LogLabel.PUBNUB_RECOVERY],
          });
          recoverForceAccessDenied().catch(() => undefined);
          return;
        }

        const matchesTarget =
          statusEvent.category === PUBNUB_CONNECTED_CATEGORY &&
          !!statusEvent.affectedChannelGroups?.includes(channelGroupString);

        debug({
          message: `Subscribe ack: category=${
            statusEvent.category
          }, operation=${
            statusEvent.operation
          }, targetMatch=${matchesTarget}, force=${!!forceReinitialize}, timedOut=${forceAckTimedOut}`,
          labels: [LogLabel.PUBNUB_RECOVERY],
        });

        if (!matchesTarget) {
          return;
        }

        if (forceAckTimeoutId !== undefined) {
          clearTimeout(forceAckTimeoutId);
          forceAckTimeoutId = undefined;
        }
        markConversationChannelGroupConnected();
        resolveForceAck?.(true);
        resolveForceAck = undefined;
      };

      // Track listeners so clearPubNub can removeListener (unsubscribeAll alone
      // leaves them attached and late status events can still fire).
      // Force path already constructed clients with the target override above;
      // normal path may need get* to materialize them from store identity.
      if (!forceReinitialize) {
        getMetaPubNub();
        getConversationPubNub();
      }
      addMetaPubNubListener(
        createPubNubMetaListeners(!!outboundChatEnabled, { isListenerCurrent }),
      );
      addConversationPubNubListener(
        createPubNubConversationListeners(
          customerId,
          orgId,
          cb,
          onConnectedAck,
          {
            skipDeliveryMarking: !!forceReinitialize,
            isListenerCurrent,
          },
        ),
      );

      if (forceReinitialize) {
        // Start the ack timeout only after auth, immediately before subscribe.
        forceAckTimeoutId = setTimeout(() => {
          forceAckTimeoutId = undefined;
          forceAckTimedOut = true;
          debug({
            message: `Force re-init ack timed out after ${FORCE_REINITIALIZE_SUBSCRIBE_ACK_TIMEOUT_MS}ms; leaving subscription alive`,
            labels: [LogLabel.PUBNUB_RECOVERY],
          });
          resolveForceAck?.(false);
          resolveForceAck = undefined;
        }, FORCE_REINITIALIZE_SUBSCRIBE_ACK_TIMEOUT_MS);

        resubscribeForceChannels();

        // Wait for the attempt-scoped ack (or timeout). Connected state is owned
        // by onConnectedAck — including late acks after timeout — so do not write
        // isPubNubConnected again here.
        await forceAckPromise;

        installRealtimeHealthChecks();
        return;
      }

      // Do not mark connected until the conversation channel group Connected ack.
      // Premature true + empty-group 400 left the widget "healthy" but deaf to
      // convo events (unfurls, agent messages) until history/poller.
      resubscribeConversationChannelGroup({ customerId, orgId });

      // Set up dependency injection for health check function
      // This allows grantToken.ts and networkStatus.ts to call the health check
      // without creating circular dependencies
      // Health check is lazy-loaded, so we pass an async wrapper function
      installRealtimeHealthChecks();

      // Optional: Check and reconnect if needed as a safeguard (Customer ID/History trigger)
      // Non-blocking call (fire-and-forget) to avoid delaying initialization callback
      // Errors are handled inside checkAndReconnectIfNeededWrapper, no need for additional error handling
      debug({
        message:
          'About to call checkAndReconnectIfNeededWrapper from pubnub/index.ts',
        labels: [LogLabel.PUBNUB_RECOVERY],
      });
      checkAndReconnectIfNeededWrapper().catch(() => {
        // Errors are already handled inside checkAndReconnectIfNeededWrapper
      });
    })
    .catch((error: unknown) => {
      // Force path: stop the grant-refresh interval started above, then restore
      // health checks. Leaving the interval alive after clearPubNub() lets
      // refreshGrantToken recreate clients from pre-merge store.customerId.
      if (forceReinitialize) {
        teardownGrantTokenRefresh();
        installRealtimeHealthChecks();
      }
      return Promise.reject(error);
    });
};
